export interface FAQ {
  question: string;
  answer: string;
}

export interface LandingPage {
  slug: string;
  path: string;
  ocasiao: string;
  destinatario?: string;
  artigo: string;        // "a" | "o"
  possessivo: string;    // "sua" | "seu"
  title: string;
  description: string;
  h1: string;
  heroSubtitle: string;
  whySection: string[];  // array de parágrafos
  priceAnchor: {
    intro: string;
    comparisons: { label: string; price: string; duracao: string; emocao: string }[];
  };
  faqs: FAQ[];
  estiloDestaque: string;
  sazonal: boolean;
  mes?: number;
}

const ALL_STYLES = [
  'Sertanejo', 'MPB', 'Gospel', 'Pop', 'Forró', 'Pagode',
  'Bossa Nova', 'Rock', 'Axé', 'Reggae', 'Jovem Guarda', 'Jazz',
  'Arrocha / Brega', 'Música Clássica', 'Infantil', 'Música Latina',
  'Rap', 'Black Music', 'Música Eletrônica', 'Bolero', 'Tango',
  'Valsa', 'Funk', 'Capoeira', 'Blues',
];

export { ALL_STYLES };

export const landingPages: LandingPage[] = [

  // ─── NÍVEL 1 ─────────────────────────────────────────────────────────────────

  {
    slug: 'presente-dia-das-maes',
    path: '/presente-dia-das-maes',
    ocasiao: 'Dia das Mães',
    artigo: 'a',
    possessivo: 'sua',
    title: 'Presente Dia das Mães — Música Personalizada | Inspira',
    description:
      'Surpreenda sua mãe no Dia das Mães com uma música feita só para ela. Entrega em até 30 min pelo WhatsApp, a partir de R$ 34,90. Refação sem custo.',
    h1: 'Presente para o Dia das Mães que ela nunca vai esquecer',
    heroSubtitle:
      'Uma canção criada com as histórias dela, a memória de vocês duas e tudo que você nunca soube como dizer. Ela vai ouvir e entender que é a música mais especial da vida dela.',
    whySection: [
      'Toda mãe já ganhou flores, chocolates, perfume. Ela agradeceu com aquele sorriso carinhoso, guardou na gaveta e a vida seguiu. Mas existe um tipo de presente que é diferente — aquele que para tudo. Que faz a mãe desligar o fogão, sentar no sofá e chorar daquele jeito bom que só o amor de filho provoca. Uma música personalizada é esse presente.',
      'A ideia é simples e poderosa ao mesmo tempo: você conta a história da sua mãe, as memórias que vocês têm juntos, o jeito que ela te criou, os sacrifícios que ela fez sem nunca reclamar, as frases que ela repete que você sabe de cor. A partir dessas histórias, nossa equipe compõe uma canção exclusiva — com a letra que fala diretamente sobre ela, no estilo musical que ela mais ama. Sertanejo raiz? MPB? Forró? A música vai soar como se fosse feita para ela desde sempre.',
      'O Dia das Mães é em maio, mas o presente certo não tem data. Você pode criar essa homenagem musical hoje, amanhã, ou no dia anterior à celebração — a entrega acontece em até 30 minutos diretamente no seu e-mail. Você abre no celular, aperta o play, e escuta o próprio nome da sua mãe na letra, escuta as memórias da família transformadas em melodia. Esse é o tipo de Dia das Mães que ela vai contar para as amigas com os olhos marejando.',
    ],
    priceAnchor: {
      intro:
        'Todo ano a mesma dúvida: o que dar para a minha mãe no Dia das Mães que seja diferente e que ela realmente goste? Compare os presentes mais comuns com uma homenagem musical:',
      comparisons: [
        { label: '💐 Buquê de flores', price: 'R$ 80–150', duracao: '3–5 dias', emocao: 'Média' },
        { label: '🌸 Perfume importado', price: 'R$ 120–400', duracao: '2–4 meses', emocao: 'Baixa' },
        { label: '🍫 Cesta de chocolates', price: 'R$ 60–120', duracao: '1–2 dias', emocao: 'Baixa' },
        { label: '👜 Bolsa ou roupa', price: 'R$ 100–500', duracao: 'Varia', emocao: 'Média' },
      ],
    },
    faqs: [
      {
        question: 'Quanto tempo antes do Dia das Mães devo pedir a música?',
        answer:
          'Você pode pedir em cima da hora sem problema — a entrega acontece em até 30 minutos após o pagamento, a qualquer hora do dia ou da noite. Mas se quiser garantir uma data específica de entrega, como o almoço do Dia das Mães, peça algumas horas antes para ter tranquilidade.',
      },
      {
        question: 'Posso incluir o nome da minha mãe e detalhes bem específicos?',
        answer:
          'Sim, é exatamente isso que torna a música especial! Quanto mais detalhes você compartilhar — o apelido que ela usa, uma frase que ela sempre repete, uma memória específica, o prato favorito dela — mais personalizada e emocionante vai ficar a letra. Nossa equipe usa tudo que você contar.',
      },
      {
        question: 'Minha mãe tem mais de 70 anos e não usa muito o celular. Como ela vai receber a música?',
        answer:
          'A música é entregue no seu e-mail — não no dela. Você recebe o arquivo em MP3 e pode fazer o que quiser: colocar para tocar na hora do almoço, mandar para o celular dela por qualquer meio, ou encaminhar pelo WhatsApp. A entrega é sempre para você.',
      },
      {
        question: 'É possível incluir uma mensagem minha junto com a música?',
        answer:
          'A letra da música já é a sua mensagem. Ela é composta com base no que você escrever sobre sua mãe, então o amor que você sente vai estar diretamente na canção. Se quiser, pode também escrever uma dedicatória separada para ler antes de colocar a música para tocar.',
      },
      {
        question: 'Posso pedir uma música com letra em outro idioma para uma mãe que veio de outro país?',
        answer:
          'Fazemos músicas em português brasileiro, mas se sua mãe veio de outro país e você quiser incluir palavras ou expressões no idioma dela na letra, basta mencionar isso ao nos contar a história. Nossa equipe vai fazer o possível para tornar a música ainda mais especial para ela.',
      },
    ],
    estiloDestaque: 'Sertanejo',
    sazonal: true,
    mes: 5,
  },

  {
    slug: 'presente-aniversario',
    path: '/presente-aniversario',
    ocasiao: 'Aniversário',
    artigo: 'o',
    possessivo: 'seu',
    title: 'Presente de Aniversário — Música Personalizada | Inspira',
    description:
      'Crie uma música personalizada de aniversário para quem você ama. Letra com a história da pessoa, 25 estilos, entrega em até 30 min. A partir de R$ 34,90.',
    h1: 'Presente de aniversário que faz todo mundo parar e escutar',
    heroSubtitle:
      'Chega de presentes que ficam no fundo da gaveta. Uma música com o nome e a história de quem você ama é o tipo de parabéns que vai ficar na memória da família para sempre.',
    whySection: [
      'Aniversário é uma data que se repete todo ano, mas que precisa ser sentida toda vez. E a grande verdade que ninguém fala é que a maioria dos presentes de aniversário não deixa marca. A roupa fica pequena, o perfume acaba, o jantar é esquecido. Mas uma música personalizada de aniversário é diferente — ela tem nome, tem história, tem o jeito único de ser da pessoa. Ela não some.',
      'Pensa comigo: imagina a pessoa que você quer homenagear ouvindo uma canção onde ela escuta o próprio nome na primeira estrofe, escuta a história que só vocês dois sabem, escuta o motivo pelo qual ela é tão especial para você. É uma experiência completamente diferente de ouvir um "parabéns pra você" genérico. É o presente que conta uma história — a história dela.',
      'Uma canção sob encomenda funciona para qualquer idade e para qualquer destinatário: o aniversário de 15 anos da filha que vira mulher, os 60 anos da mãe que merece ser celebrada, os 30 anos do amigo de infância, a festa surpresa do marido. Para cada pessoa, uma música diferente — e para cada ocasião, um estilo que combina com ela. Você escolhe o gênero musical, a voz, e conta a história. Em até 30 minutos, a homenagem musical está pronta no seu e-mail.',
    ],
    priceAnchor: {
      intro:
        'Todo ano a mesma pergunta: o que dar de presente de aniversário que seja diferente? Veja como uma música personalizada se compara com os presentes mais dados:',
      comparisons: [
        { label: '🎂 Bolo personalizado', price: 'R$ 80–200', duracao: '1 dia', emocao: 'Média' },
        { label: '💐 Flores e chocolates', price: 'R$ 80–150', duracao: '3–5 dias', emocao: 'Média' },
        { label: '🍽️ Jantar especial', price: 'R$ 100–400', duracao: '2 horas', emocao: 'Alta' },
        { label: '📸 Álbum de fotos', price: 'R$ 80–200', duracao: 'Para sempre', emocao: 'Alta' },
      ],
    },
    faqs: [
      {
        question: 'Posso pedir a música com poucos dias de antecedência?',
        answer:
          'Pode pedir até horas antes! A entrega acontece em até 30 minutos após a confirmação do pagamento, então mesmo que você lembre do aniversário de última hora, ainda dá tempo de presentear com uma música exclusiva. Muitas clientes pedem no próprio dia do aniversário.',
      },
      {
        question: 'A música fica com o nome da pessoa ou é mais genérica?',
        answer:
          'A letra é criada especialmente com base na história que você conta. O nome da pessoa homenageada aparece na música, assim como detalhes específicos que você compartilhar: apelidos, memórias, características marcantes. Quanto mais você contar, mais personalizada e emocionante fica a canção.',
      },
      {
        question: 'Posso usar a música na festa de aniversário?',
        answer:
          'Com certeza! Muitas pessoas colocam a música para tocar na hora do parabéns, como abertura da festa ou em um vídeo de homenagem. O arquivo MP3 que você recebe pode ser usado em qualquer situação pessoal — festa, vídeo, stories, cerimônia. A licença de uso pessoal está inclusa.',
      },
      {
        question: 'E se eu quiser a música em português com palavras em outro idioma?',
        answer:
          'Fazemos a música em português brasileiro, mas se a pessoa tem um apelido em outro idioma, ou se existe uma frase especial entre vocês em outra língua, basta mencionar isso ao contar a história. Faremos o possível para incorporar de forma natural na letra.',
      },
      {
        question: 'Funciona para aniversário de criança também?',
        answer:
          'Sim! Temos o estilo musical "Infantil" especialmente para músicas para crianças — letra leve, divertida, com o nome e o jeitinho da criança. Ideal para presentear uma criança no aniversário ou para pais e avós que querem homenagear a criança da família.',
      },
    ],
    estiloDestaque: 'MPB',
    sazonal: false,
  },

  {
    slug: 'presente-casamento',
    path: '/presente-casamento',
    ocasiao: 'Casamento',
    artigo: 'o',
    possessivo: 'seu',
    title: 'Presente de Casamento — Música Personalizada | Inspira',
    description:
      'Crie uma música exclusiva para o casamento dos noivos. Letra com a história do casal, entrega em até 30 min, a partir de R$ 34,90. Um presente para a vida toda.',
    h1: 'Presente de casamento que toca o coração dos noivos',
    heroSubtitle:
      'Uma canção criada com a história do amor dos dois — o jeito que eles se conheceram, o que os une, o que os faz rir. Um presente que vai emocionar não só os noivos, mas todos que estiverem presentes.',
    whySection: [
      'Na lista de casamento, você encontra panelas, copos, travesseiros e centenas de itens práticos. E a maioria dos convidados fica em dúvida: dá o dinheiro, compra algo da lista ou busca um presente criativo que realmente surpreenda? Uma música personalizada é a resposta para quem quer dar um presente de casamento que ninguém vai dar igual — e que os noivos vão guardar para sempre.',
      'A homenagem musical para casamento é especial por um motivo simples: ela conta a história dos dois. Você fala sobre como eles se conheceram, sobre o que os une, sobre as qualidades que cada um admira no outro, sobre os momentos que marcaram esse relacionamento. Nossa equipe transforma tudo isso em uma canção exclusiva, no estilo musical que o casal ama, com a letra que vai fazer os dois olharem um para o outro com aquele amor que não precisa de palavras.',
      'Uma canção exclusiva pode ser usada na entrada da noiva, como trilha sonora da valsa, no vídeo de homenagem, ou simplesmente ouvida na lua de mel. É um presente que não ocupa espaço na mudança, não vai para a brechó depois de um ano e não duplica com mais ninguém da lista de convidados. Em até 30 minutos após o pagamento, você recebe o arquivo MP3 no seu e-mail — pronto para presentear na véspera ou no dia do casamento.',
    ],
    priceAnchor: {
      intro:
        'Presentes de casamento podem sair caro. Veja como uma música personalizada se compara — e por que ela vale muito mais do que o preço sugere:',
      comparisons: [
        { label: '🎁 Item da lista de casamento', price: 'R$ 80–300', duracao: 'Varia', emocao: 'Baixa' },
        { label: '💵 Dinheiro em envelope', price: 'R$ 50–200', duracao: '1 mês', emocao: 'Baixa' },
        { label: '🍾 Garrafa de vinho especial', price: 'R$ 80–250', duracao: '1 noite', emocao: 'Média' },
        { label: '📸 Álbum de fotos do casal', price: 'R$ 100–300', duracao: 'Para sempre', emocao: 'Alta' },
      ],
    },
    faqs: [
      {
        question: 'Posso pedir a música para a entrada da noiva ou para a valsa?',
        answer:
          'Sim, muitos clientes fazem exatamente isso. Você recebe um arquivo MP3 que pode ser colocado em qualquer playlist ou dispositivo de som. Para usar em cerimônias, recomendamos pedir com alguns dias de antecedência para ter tempo de testar e ajustar o volume com o DJ ou cerimonial.',
      },
      {
        question: 'Posso contar a história dos dois sem saber todos os detalhes?',
        answer:
          'Quanto mais detalhes você tiver, melhor — mas não precisa ser perfeito. Pode contar como eles se conheceram, o que você admira nos dois como casal, uma memória especial que viveram juntos, ou simplesmente o que eles representam um para o outro. Nossa equipe sabe como transformar qualquer história em algo emocionante.',
      },
      {
        question: 'É possível pedir a música como surpresa sem o casal saber?',
        answer:
          'É exatamente assim que a maioria das pessoas faz! Você pede a música sem que os noivos saibam, e apresenta como surpresa na festa, na lua de mel ou como presente antes da cerimônia. A música chega no seu e-mail — nenhum contato com os noivos é necessário.',
      },
      {
        question: 'Posso pedir uma música para presentear o noivo ou a noiva individualmente?',
        answer:
          'Claro! Às vezes um familiar quer homenagear a filha no casamento, ou um amigo quer celebrar o amigo que está se casando. A música pode ser voltada para um dos dois ou para o casal — é você quem define o foco ao contar a história.',
      },
      {
        question: 'Funciona para bodas de casamento também?',
        answer:
          'Com certeza! Uma música personalizada é um presente lindo para bodas de prata (25 anos), bodas de ouro (50 anos) ou qualquer aniversário de casamento. É uma das formas mais bonitas de celebrar que um amor resistiu ao tempo e se tornou ainda mais forte.',
      },
    ],
    estiloDestaque: 'MPB',
    sazonal: false,
  },

  {
    slug: 'presente-natal',
    path: '/presente-natal',
    ocasiao: 'Natal',
    artigo: 'o',
    possessivo: 'seu',
    title: 'Presente de Natal — Música Personalizada Inesquecível | Inspira',
    description:
      'Dê no Natal uma música personalizada para quem você ama. Letra com a história da família, 25 estilos, entrega em até 30 min. A partir de R$ 34,90.',
    h1: 'Presente de Natal que une a família em torno de uma música',
    heroSubtitle:
      'Natal é a época de reunir quem você ama. E uma música com a história da família — os nomes, as memórias, o amor que une todo mundo — é o presente que vai fazer essa reunião ficar na memória de todos.',
    whySection: [
      'O Natal é a época do ano em que todo mundo quer dar um presente especial mas não sabe muito bem o que escolher. Para as crianças é fácil — mas e para a mãe, para a avó, para o sogro que tem tudo? Uma música personalizada resolve exatamente esse dilema. É um presente que não tem tamanho, não tem idade certa e não precisa de nota fiscal para trocar.',
      'Imagine a ceia de Natal: todo mundo reunido, a sobremesa na mesa, e alguém coloca uma música para tocar. A letra começa, as pessoas reconhecem os próprios nomes, escutam as memórias de família dentro da canção — aquela viagem que fizeram juntos, o apelido que só a família conhece, a frase que a avó sempre repete. É o tipo de momento que para o tempo. Os celulares saem do bolso para filmar, mas ninguém consegue ficar seco de olho.',
      'Uma canção natalina personalizada pode ser feita para presentear uma pessoa específica — a mãe, o pai, o filho, a parceira — ou para homenagear a família inteira. Você conta a história, escolhe o estilo musical (sertanejo natalino? MPB? Gospel?), e em até 30 minutos o arquivo MP3 está no seu e-mail, pronto para ser a trilha sonora da noite de Natal mais especial que sua família já teve.',
    ],
    priceAnchor: {
      intro:
        'No Natal, a tentação é gastar mais do que o necessário em presentes que logo são esquecidos. Compare com uma homenagem musical:',
      comparisons: [
        { label: '🎁 Brinquedo ou game', price: 'R$ 100–500', duracao: '1–6 meses', emocao: 'Média' },
        { label: '👕 Roupa ou acessório', price: 'R$ 80–300', duracao: 'Varia', emocao: 'Baixa' },
        { label: '📱 Eletrônico', price: 'R$ 200–2000', duracao: '2–4 anos', emocao: 'Baixa' },
        { label: '🍫 Cesta de Natal', price: 'R$ 80–200', duracao: '1–2 semanas', emocao: 'Baixa' },
      ],
    },
    faqs: [
      {
        question: 'Posso pedir a música na véspera de Natal e ainda receber a tempo?',
        answer:
          'Sim! A entrega é em até 30 minutos após a confirmação do pagamento, a qualquer hora. Pode pedir no dia 24 à tarde, na noite de Natal ou quando quiser — a música chega no seu e-mail em poucos minutos.',
      },
      {
        question: 'A música pode ser sobre a família inteira, não só uma pessoa?',
        answer:
          'Claro! Muitas pessoas pedem uma música que celebre a família — com os nomes dos membros, as memórias que compartilham, o espírito da reunião de Natal. Basta contar a história da família ao fazer o pedido.',
      },
      {
        question: 'Posso pedir uma música com temática natalina na letra?',
        answer:
          'Sim! Se quiser que a letra tenha referências ao Natal — como a noite de ceia, a reunião de família, o espírito de gratidão da época — basta mencionar isso ao contar a história. A equipe vai incorporar esses elementos de forma natural.',
      },
      {
        question: 'É possível dar a música como presente para alguém que mora em outro estado?',
        answer:
          'A entrega é por e-mail, então funciona para qualquer lugar do Brasil. Você recebe o arquivo e pode repassar por qualquer meio — WhatsApp, Bluetooth, ou outro aplicativo. Ou pode dar como surpresa colocando a música para tocar na chamada de vídeo de Natal.',
      },
      {
        question: 'Posso pedir mais de uma música — uma para cada pessoa da família?',
        answer:
          'Sim! Cada música é cobrada à parte, a partir de R$ 34,90, e criada de forma totalmente independente. Muitas pessoas aproveitam para fazer uma música para a mãe, uma para o pai e uma para o cônjuge no mesmo Natal. Para pedidos múltiplos, fale com a gente pelo WhatsApp.',
      },
    ],
    estiloDestaque: 'Gospel',
    sazonal: true,
    mes: 12,
  },

  {
    slug: 'presente-dia-dos-namorados',
    path: '/presente-dia-dos-namorados',
    ocasiao: 'Dia dos Namorados',
    artigo: 'o',
    possessivo: 'seu',
    title: 'Presente Dia dos Namorados — Música Personalizada | Inspira',
    description:
      'Surpreenda seu amor no Dia dos Namorados com uma música feita com a história de vocês. A partir de R$ 34,90, com entrega em até 30 min pelo WhatsApp.',
    h1: 'Presente para o Dia dos Namorados que diz tudo que você nunca conseguiu colocar em palavras',
    heroSubtitle:
      'Uma música criada com a história de vocês dois — como se conheceram, o que você admira nele ou nela, os momentos que fazem esse amor ser diferente de qualquer outro. Um presente que ela ou ele vai escutar para sempre.',
    whySection: [
      'Todo Dia dos Namorados, a mesma angústia: flores acabam em dias, jantar é caro e genérico, caixa de chocolate a gente come em um final de semana. E o que fica depois disso tudo? Pouquíssimo. Mas existe um presente que é completamente diferente — uma música personalizada com a história de vocês dois. Esse tipo de homenagem musical não some, não estraga e não vai para a lixeira.',
      'Funciona assim: você conta para a nossa equipe como vocês se conheceram, o que você mais ama na outra pessoa, as memórias que só vocês dois têm, os planos que constroem juntos. Com base nessa história, componemos uma canção exclusiva, no estilo musical que a pessoa amada adora — pop romântico, sertanejo universitário, MPB, pagode, o que fizer mais sentido para ela. A letra vai ter o nome dela, vai ter a sua história dentro, vai ter o amor que você sente transformado em música.',
      'E a melhor parte? Você pode colocar para tocar no jantar dos namorados, mandar pelo WhatsApp como surpresa quando ela menos esperar, usar na chamada de vídeo se vocês estiverem longe, ou guardar para sempre como a trilha sonora oficial do relacionamento. Em até 30 minutos após o pagamento, a música está pronta. Esse é o Dia dos Namorados que ela vai contar para as amigas dela com orgulho.',
    ],
    priceAnchor: {
      intro:
        'No Dia dos Namorados, os presentes tradicionais custam mais e ficam menos. Veja como uma canção exclusiva se compara:',
      comparisons: [
        { label: '🌹 Buquê de rosas', price: 'R$ 80–200', duracao: '5–7 dias', emocao: 'Alta' },
        { label: '🍷 Jantar romântico', price: 'R$ 150–400', duracao: '2–3 horas', emocao: 'Alta' },
        { label: '🌸 Perfume importado', price: 'R$ 120–400', duracao: '2–4 meses', emocao: 'Média' },
        { label: '💍 Joia ou acessório', price: 'R$ 150–800', duracao: 'Para sempre', emocao: 'Alta' },
      ],
    },
    faqs: [
      {
        question: 'Como faço para que a música seja uma surpresa total?',
        answer:
          'Peça a música sem mencionar para a pessoa. O arquivo chega no seu e-mail — não no dela. Você escolhe quando, como e onde colocar para tocar. Pode mandar por mensagem com uma dedicatória, colocar para tocar no jantar, ou apresentar numa chamada de vídeo. A surpresa é 100% controlada por você.',
      },
      {
        question: 'E se eu quiser uma música de amor mais intensa ou mais divertida?',
        answer:
          'Você define o tom ao contar a história. Se o relacionamento de vocês é mais apaixonado e intenso, mencione isso. Se vocês têm mais química de parceiros de vida e humor, fale sobre isso também. Nossa equipe adapta o tom da letra — pode ser emocionante, romantica, bem-humorada, ou uma mistura dos três.',
      },
      {
        question: 'Funciona para casais que namoram à distância?',
        answer:
          'É um dos usos mais lindos! Como a entrega é por e-mail, vocês podem estar em cidades — ou até países — diferentes, e ainda assim compartilhar esse momento especial em tempo real. Você recebe o MP3, abre na chamada de vídeo e coloca para tocar para ela ao vivo no Dia dos Namorados.',
      },
      {
        question: 'Posso pedir uma música mais descontraída, estilo brincadeira entre nós dois?',
        answer:
          'Com certeza! Além dos estilos mais românticos, você pode pedir um funk, um pagode ou até um rap com tom bem-humorado. Basta mencionar que quer uma abordagem mais leve e contar as histórias engraçadas e as brincadeiras que vocês têm. Pode sair muito fofo e divertido.',
      },
      {
        question: 'Posso pedir para meu noivo ou namorado criar uma música pra mim sem ele saber?',
        answer:
          'Você pode, mas nesse caso você é quem está pedindo a homenagem para a outra pessoa. Se quiser que ele crie a música para você de surpresa, infelizmente precisaria que ele mesmo fizesse o pedido. Mas que tal presentear ele primeiro e inspirá-lo a fazer o mesmo por você?',
      },
    ],
    estiloDestaque: 'Sertanejo',
    sazonal: true,
    mes: 6,
  },

  {
    slug: 'presente-dia-dos-pais',
    path: '/presente-dia-dos-pais',
    ocasiao: 'Dia dos Pais',
    artigo: 'o',
    possessivo: 'seu',
    title: 'Presente Dia dos Pais — Música Personalizada | Inspira Músicas',
    description:
      'Homenageie seu pai no Dia dos Pais com uma música feita com a história de vocês. Forró, Sertanejo, MPB. Entrega em até 30 min. A partir de R$ 34,90.',
    h1: 'Presente para o Dia dos Pais que fala tudo que a gente não sabe dizer',
    heroSubtitle:
      'Pai não chora, não fala muito de sentimento, mas tem coisa que entra pelo ouvido e vai direto para o coração. Uma música com a história dele — a vida que ele construiu, o exemplo que deu, o amor que tem por dentro — é o presente que vai fazer ele engolir o choro com orgulho.',
    whySection: [
      'Pai é daquele tipo de pessoa que recebe presente e fala "não precisava, meu filho". Mas existe um presente que ele não vai conseguir disfarçar a emoção: uma música personalizada com a história dele. O homem que não chora em filme, que não fala muito de sentimentos, que prefere um abraço rápido a um discurso longo — esse pai vai sentar e escutar a própria história em forma de canção sem conseguir fingir que está bem.',
      'A homenagem musical para o Dia dos Pais funciona de um jeito especial: você conta quem é seu pai. O trabalho que ele fez, o sacrifício que fez em silêncio, as lições que passou sem dar sermão, as memórias que vocês têm juntos — aquela pescaria, aquela viagem, aquele jogo de futebol, aquele momento em que ele apareceu quando você mais precisava. Nossa equipe compõe uma canção com tudo isso, no estilo que ele ama: forró, sertanejo raiz, MPB, pagode, o gênero musical que faz parte da identidade dele.',
      'O Dia dos Pais é em agosto, mas você pode criar essa homenagem a qualquer momento — não precisa esperar uma data específica para dizer ao seu pai o quanto ele importa. A entrega é em até 30 minutos após o pagamento, direto no seu e-mail. Ligue para ele, conte que tem uma surpresa, e coloque a música para tocar. Pode ser ao telefone, pode ser na mesa do almoço, pode ser com a família toda reunida. A cena vai ficar registrada na memória de todo mundo.',
    ],
    priceAnchor: {
      intro:
        'Presente de Dia dos Pais costuma ser camisa, perfume ou kit de churrasco. Uma canção personalizada é diferente — e custa menos do que a maioria:',
      comparisons: [
        { label: '👕 Camisa ou roupa', price: 'R$ 80–200', duracao: '6–12 meses', emocao: 'Baixa' },
        { label: '🌸 Perfume masculino', price: 'R$ 100–400', duracao: '3–6 meses', emocao: 'Baixa' },
        { label: '🍖 Kit churrasco', price: 'R$ 80–250', duracao: '1 dia', emocao: 'Média' },
        { label: '⌚ Relógio ou acessório', price: 'R$ 100–500', duracao: 'Para sempre', emocao: 'Média' },
      ],
    },
    faqs: [
      {
        question: 'E se meu pai não usa WhatsApp ou celular?',
        answer:
          'Sem problema — a entrega é no seu e-mail, não no dele. Você recebe o arquivo em MP3 e pode fazer como quiser: tocar em um som, em um tablet, transferir para o celular dele, colocar em um pendrive para ele ouvir no carro. A música é sua para usar da forma que preferir.',
      },
      {
        question: 'Posso incluir histórias de quando eu era criança junto com ele?',
        answer:
          'Sim, e essas são as melhores histórias! Memórias da infância — aquele apelido que ele tinha para você, aquela brincadeira de fim de semana, a vez que ele te ensinou algo importante — tornam a música muito mais pessoal e emocionante. Quanto mais específico você for, mais especial fica.',
      },
      {
        question: 'Funciona para homenagear um padrasto ou pai adotivo?',
        answer:
          'Com certeza. Pai é quem cria, quem está presente, quem dá o exemplo. Se a pessoa que você quer homenagear é um padrasto, pai adotivo, ou qualquer figura paterna que foi importante na sua vida, essa homenagem musical é feita para ela. Basta contar a história como ela é.',
      },
      {
        question: 'Posso pedir uma música mais engraçada, que brinque com as manias do meu pai?',
        answer:
          'Pode sim! Existem pais que adoram uma brincadeira boa e que vão rir muito de uma música que mencione as manias deles — a teimosa do jeito que dirige, o chiado que faz quando quer silêncio, o gosto musical que os filhos não entendem. Basta contar essas histórias e deixar claro que quer um tom bem-humorado.',
      },
      {
        question: 'Meu pai já faleceu. Posso fazer uma música em homenagem à memória dele?',
        answer:
          'Sim, e é uma das formas mais bonitas de preservar a memória de quem partiu. Uma canção de homenagem póstuma pode ser compartilhada com toda a família, tocada em datas especiais, ou simplesmente guardada como um tributo ao legado dele. Basta contar a história com amor — o resto a gente faz.',
      },
    ],
    estiloDestaque: 'Forró',
    sazonal: true,
    mes: 8,
  },

  {
    slug: 'presente-formatura',
    path: '/presente-formatura',
    ocasiao: 'Formatura',
    artigo: 'a',
    possessivo: 'sua',
    title: 'Presente de Formatura — Música Personalizada | Inspira Músicas',
    description:
      'Celebre a formatura com uma música exclusiva sobre a jornada do formando. Entrega em até 30 min pelo WhatsApp, a partir de R$ 34,90. Um presente para a vida.',
    h1: 'Presente de formatura que celebra a jornada, não só a conquista',
    heroSubtitle:
      'Anos de estudo, abdicação, noites viradas e choro no banheiro — e agora a conquista. Uma música personalizada com a história dessa jornada é o presente que vai emocionar o formando e todo mundo ao redor.',
    whySection: [
      'Formatura é uma das conquistas mais significativas da vida de uma pessoa. É o resultado de anos de esforço, de abrir mão de fins de semana, de sacrifícios que às vezes passam despercebidos mas que os familiares mais próximos viram de perto. Um presente de formatura precisa estar à altura dessa conquista — e uma música personalizada é exatamente isso.',
      'Enquanto a maioria das pessoas dá dinheiro ou objetos práticos para o formando, uma homenagem musical conta a história real: quem esse formando é, de onde veio, o que passou para chegar até aqui. A letra da canção pode mencionar o curso que escolheu com tanto amor, as madrugadas de estudo, o apoio da família, os amigos que ficaram pelo caminho, o momento em que quase desistiu mas continuou. Tudo isso transformado em música, no estilo que o formando ama.',
      'Uma canção exclusiva de formatura pode ser tocada na entrada da cerimônia, como abertura do baile, em um vídeo de homenagem, ou simplesmente entregue como presente pessoal antes da festa. Em até 30 minutos após o pagamento, o arquivo MP3 está no seu e-mail. É o presente que vai ser ouvido depois da formatura, no primeiro emprego, na pós-graduação, daqui a dez anos quando a pessoa olhar para trás e lembrar de onde veio.',
    ],
    priceAnchor: {
      intro:
        'Presente de formatura costuma ser dinheiro ou coisa prática para o apartamento novo. Veja como uma música personalizada se destaca:',
      comparisons: [
        { label: '💵 Dinheiro', price: 'R$ 50–500', duracao: '1 semana', emocao: 'Baixa' },
        { label: '🛋️ Objeto para o apê', price: 'R$ 100–400', duracao: 'Varia', emocao: 'Baixa' },
        { label: '✈️ Viagem de formatura', price: 'R$ 2000–8000', duracao: '1 semana', emocao: 'Alta' },
        { label: '📸 Álbum de fotos da jornada', price: 'R$ 100–300', duracao: 'Para sempre', emocao: 'Alta' },
      ],
    },
    faqs: [
      {
        question: 'Posso pedir a música para ser tocada na cerimônia de formatura?',
        answer:
          'Sim! A música chega em MP3 e pode ser tocada em qualquer sistema de som. Para uso em cerimônias, recomendamos pedir com alguns dias de antecedência para ter tempo de testar com a equipe de som do evento. Muitos formandos usam a música na entrada, na valsa de formatura ou como abertura de homenagem.',
      },
      {
        question: 'A música pode contar a história do grupo de amigos da turma?',
        answer:
          'Claro! Uma música de homenagem de turma é muito especial — ela pode mencionar os professores, os momentos marcantes do curso, os apelidos da turma, as memórias compartilhadas. É o tipo de presente coletivo que o grupo pode dividir o custo e usar como trilha sonora da formatura.',
      },
      {
        question: 'Funciona para homenagear um familiar que se formou, não só o próprio formando?',
        answer:
          'Com certeza. Pais, irmãos e amigos pedem a música para homenagear o formando. Você conta a história de fora — o que você viu essa pessoa passar, como ela cresceu, o orgulho que você sente. Às vezes a perspectiva de quem acompanhou de perto emociona ainda mais do que o próprio formando esperaria.',
      },
      {
        question: 'Pode ser para qualquer tipo de formatura — médico, advogado, técnico, curso livre?',
        answer:
          'Qualquer conquista merece ser celebrada. Seja uma graduação, um curso técnico, uma pós-graduação, um MBA ou até um curso profissionalizante que mudou a vida da pessoa — o que importa é a história por trás da conquista. Essa é a história que a música vai contar.',
      },
      {
        question: 'Posso incluir uma mensagem para o futuro na letra?',
        answer:
          'Sim! Muitas músicas de formatura têm esse tom prospectivo — celebram o que foi conquistado e apontam para o futuro com esperança e amor. Se quiser que a letra tenha esse aspecto, basta mencionar ao contar a história e incluir o que você deseja para o futuro dessa pessoa.',
      },
    ],
    estiloDestaque: 'Pop',
    sazonal: false,
  },

  // ─── NÍVEL 2 ─────────────────────────────────────────────────────────────────

  {
    slug: 'presente-dia-das-maes-para-mae',
    path: '/presente-dia-das-maes-para-mae',
    ocasiao: 'Dia das Mães',
    destinatario: 'mãe',
    artigo: 'a',
    possessivo: 'sua',
    title: 'Música Personalizada para sua Mãe no Dia das Mães | Inspira',
    description:
      'Crie uma música especial para sua mãe no Dia das Mães. Letra com a história dela, sertanejo, MPB, gospel e mais. Entrega em até 30 min. A partir de R$ 34,90.',
    h1: 'Uma música feita só para a sua mãe no Dia das Mães',
    heroSubtitle:
      'Ela passou anos fazendo tudo por você. Chegou a hora de fazer algo que seja totalmente sobre ela — a história dela, o amor dela, o jeito único dela de ser mãe. Uma canção que ela vai ouvir e saber que foi você que mandou fazer.',
    whySection: [
      'A mãe é aquela pessoa que raramente coloca ela mesma em primeiro lugar. Ela cuida, ela dá, ela se preocupa — e quando chega o Dia das Mães, muitas vezes o presente que ela recebe é bonito mas genérico, como se você tivesse comprado no corredor do supermercado sem pensar muito. Uma música personalizada para a sua mãe muda isso. É um presente que diz: eu pensei em você especificamente, eu parei para lembrar das nossas histórias, eu quis fazer algo que só você poderia receber.',
      'A canção para a sua mãe é criada com base no que você contar sobre ela: o jeito que ela te criou, as frases que ela repete que você sabe de cor, o sacrifício que ela fez que você só entendeu quando cresceu, as memórias da infância que ficaram gravadas na sua memória. Nossa equipe transforma tudo isso em uma letra que soa como se você mesmo tivesse escrito — só que com melodia, com arranjo, no estilo musical que ela ama. Sertanejo raiz? MPB dos anos 80? Gospel? A música vai ter a cara dela.',
      'Muitas filhas chegam na hora do presente e travam. Não é falta de amor — é o contrário. O amor é tão grande que parece que nada é suficiente para expressá-lo. Uma homenagem musical é uma forma de dizer tudo que às vezes fica preso na garganta. Ela escuta, fecha os olhos, e sabe. Você não vai precisar de mais nenhuma palavra depois que a música tocar.',
    ],
    priceAnchor: {
      intro:
        'Para a mãe que merece o melhor, vale a pena comparar o que dura e o que realmente emociona:',
      comparisons: [
        { label: '💐 Buquê especial', price: 'R$ 100–250', duracao: '5–7 dias', emocao: 'Alta' },
        { label: '🌸 Kit de beleza', price: 'R$ 80–300', duracao: '1–3 meses', emocao: 'Média' },
        { label: '💆 Dia de spa', price: 'R$ 150–400', duracao: '1 dia', emocao: 'Alta' },
        { label: '🧣 Roupa ou acessório', price: 'R$ 80–400', duracao: 'Varia', emocao: 'Baixa' },
      ],
    },
    faqs: [
      {
        question: 'Minha mãe não entende muito de tecnologia. Como ela vai ouvir a música?',
        answer:
          'A música chega em MP3 no seu e-mail. Você controla como ela vai chegar para a sua mãe: pode encaminhar por WhatsApp, colocar para tocar no celular, transferir para um pen drive, gravar em um CD, ou simplesmente abrir no caixa de som quando ela estiver ao lado. Você decide o momento e o formato.',
      },
      {
        question: 'Posso pedir a música junto com um vídeo de fotos?',
        answer:
          'Nós entregamos apenas a música em MP3, mas muitos filhos combinam a música com um vídeo de fotos feito por eles mesmos — usando aplicativos gratuitos como o CapCut, por exemplo. A música funciona como trilha sonora do vídeo. É uma combinação muito bonita.',
      },
      {
        question: 'E se minha mãe não gostar do estilo musical que escolhi?',
        answer:
          'Recomendamos sempre escolher o estilo que ela mesma gosta, não o seu favorito. Se não tiver certeza, prefira estilos mais populares como sertanejo, MPB ou gospel. Mas se por algum motivo ela não gostar da música, nossa garantia cobre — refazemos ou devolvemos o valor em até 7 dias.',
      },
      {
        question: 'Posso incluir uma frase ou poesia que eu já escrevi para ela?',
        answer:
          'Pode! Se você já tem uma frase, um trecho de poema ou uma mensagem que quer que apareça na letra, basta incluir ao contar a história. Nossa equipe vai incorporar de forma natural na composição.',
      },
      {
        question: 'A música pode mencionar outros filhos ou familiares junto comigo?',
        answer:
          'Claro! Se você quer que a letra mencione que são vários filhos se unindo para fazer essa homenagem, ou que o pai também contribuiu, basta informar. A letra pode ser da perspectiva de um filho só ou de toda a família — você decide.',
      },
    ],
    estiloDestaque: 'Sertanejo',
    sazonal: true,
    mes: 5,
  },

  {
    slug: 'presente-aniversario-para-esposa',
    path: '/presente-aniversario-para-esposa',
    ocasiao: 'Aniversário',
    destinatario: 'esposa',
    artigo: 'a',
    possessivo: 'sua',
    title: 'Música Personalizada de Aniversário para a Esposa | Inspira',
    description:
      'Surpreenda sua esposa no aniversário com uma música criada com a história de vocês. A partir de R$ 34,90, com entrega em até 30 min. Um presente que ela nunca vai esquecer.',
    h1: 'Presente de aniversário para a esposa que prova que você se lembrou de cada detalhe',
    heroSubtitle:
      'Ela já ganhou flores, joias e jantares. Mas uma música criada com a história de vocês dois — a primeira vez que se olharam, o que a faz rir, o que você admira nela — é o presente que vai fazer ela lembrar por que escolheu você.',
    whySection: [
      'Chega uma hora no casamento em que presentear a esposa no aniversário se torna um desafio sério. Você já deu flores, já teve a surpresa do jantar, já escolheu a joia. E ao mesmo tempo quer dar algo que diga: eu ainda te vejo, ainda penso em você, ainda me importo com os detalhes. Uma música personalizada faz exatamente isso. É um presente que só existe porque você parou, pensou nela especificamente, e quis fazer algo que só ela poderia receber.',
      'A canção para a sua esposa é composta com base na história de vocês como casal e em quem ela é como pessoa. Você conta: como ela é, o que você admira nela, um momento marcante do relacionamento, aquela coisa que ela faz que só ela faz. Nossa equipe transforma isso numa letra que vai fazer ela perceber que você não está só celebrando mais um aniversário — está celebrando ela. Com nome na letra, com as memórias de vocês dentro da canção, no estilo musical que ela ama.',
      'É o tipo de presente que funciona especialmente bem porque quebra o padrão. Enquanto todo mundo dá o presente convencional, você apresenta uma canção exclusiva. E o melhor: você não precisa de meses de planejamento. A entrega é em até 30 minutos após o pagamento. Você pode pedir de manhã e ter a música pronta para o almoço de aniversário. Ou pedir durante o dia e deixar como surpresa para a noite.',
    ],
    priceAnchor: {
      intro:
        'Para a esposa que merece muito, veja como uma música personalizada se compara com os presentes mais comuns:',
      comparisons: [
        { label: '🌹 Buquê de rosas', price: 'R$ 100–300', duracao: '5–7 dias', emocao: 'Alta' },
        { label: '💍 Joia ou acessório', price: 'R$ 150–1000', duracao: 'Para sempre', emocao: 'Alta' },
        { label: '🍷 Jantar romântico', price: 'R$ 150–500', duracao: '2–3 horas', emocao: 'Alta' },
        { label: '🌸 Perfume importado', price: 'R$ 150–500', duracao: '3–6 meses', emocao: 'Média' },
      ],
    },
    faqs: [
      {
        question: 'Posso pedir sem que ela saiba e revelar na hora do parabéns?',
        answer:
          'Exatamente como funciona! Você faz o pedido, recebe a música no seu e-mail, e escolhe o momento perfeito para revelar — pode ser na hora do parabéns, durante o jantar, ou até como mensagem de bom dia. A surpresa é completamente controlada por você.',
      },
      {
        question: 'A música pode falar sobre o relacionamento e não só sobre ela?',
        answer:
          'Sim! Você define o foco ao contar a história. Pode ser uma música sobre ela — as qualidades dela, o que você ama nela. Pode ser sobre vocês dois — a história do casal, o que construíram juntos. Ou pode ser uma mistura dos dois. Basta deixar claro na hora de contar a história.',
      },
      {
        question: 'Posso fazer uma música mais divertida, que brinque com as manias dela?',
        answer:
          'Pode! Uma música de aniversário bem-humorada que mencione as manias adoráveis da esposa pode ser muito mais emocionante e autêntica do que um balada genérica. Se vocês têm esse tipo de relacionamento com humor, basta contar as histórias engraçadas e pedir um tom mais leve.',
      },
      {
        question: 'É possível usar a música como abertura de uma festa surpresa?',
        answer:
          'Sim! Muitos maridos colocam a música para tocar quando a esposa entra na festa surpresa. Imagine ela entrando e escutando o próprio nome na letra, com as memórias do casal dentro da canção. É o tipo de abertura que faz todo mundo parar.',
      },
      {
        question: 'E se ela não gostar do estilo musical que escolhi?',
        answer:
          'Recomendamos sempre escolher o estilo favorito dela, não o seu. Se não tiver certeza, pergunte de um jeito que não entregue a surpresa — ou escolha um estilo neutro e emocional como MPB ou pop romântico. Se por alguma razão ela não gostar, a garantia cobre: refazemos ou devolvemos.',
      },
    ],
    estiloDestaque: 'MPB',
    sazonal: false,
  },

  {
    slug: 'presente-aniversario-para-marido',
    path: '/presente-aniversario-para-marido',
    ocasiao: 'Aniversário',
    destinatario: 'marido',
    artigo: 'o',
    possessivo: 'seu',
    title: 'Música Personalizada de Aniversário para o Marido | Inspira',
    description:
      'Crie uma música para o aniversário do seu marido com a história de vocês. A partir de R$ 34,90, com entrega em até 30 min pelo WhatsApp. Ele vai se emocionar.',
    h1: 'Presente de aniversário para o marido que vai fazer ele engolir o choro',
    heroSubtitle:
      'Homem que não chora em filme vai travar quando ouvir a própria história em forma de música — com o nome dele na letra, com as memórias de vocês dentro da canção. Esse é o aniversário que ele vai lembrar para sempre.',
    whySection: [
      'Presentear marido no aniversário tem seus desafios específicos. Ele tem tudo que precisa, não usa perfume que você compraria, e quando você pergunta o que ele quer, ele diz "não precisa dar nada". Mas existe uma categoria de presente que rompe com toda essa resistência: uma música com a história dele e de vocês dois. Não tem como fingir que não está emocionado quando escuta o próprio nome numa letra que conta quem ele é.',
      'Uma canção exclusiva para o marido funciona de um jeito poderoso: você conta o que ele representa para você, as memórias que marcaram o casamento, o jeito que ele cuida da família, o que ele fez em momentos difíceis, as coisas que ele faz que ele nem percebe que você nota. Nossa equipe compõe a música com base nessa história — com a letra que ele vai reconhecer como a história real de vocês, no estilo musical que ele ama. Sertanejo? Pagode? Forró? Rock? A música vai soar como se fosse feita para ele.',
      'O melhor dos presentes é aquele que não precisou de muito dinheiro, mas que exigiu atenção, cuidado e amor para escolher. Uma homenagem musical de aniversário para o marido diz: eu me lembro de nós, eu vejo quem você é, e eu quis fazer algo especial que só você poderia receber. Em até 30 minutos após o pagamento, a música está pronta. É o aniversário que ele vai comentar com os amigos.',
    ],
    priceAnchor: {
      intro:
        'Presente para marido no aniversário costuma ser difícil. Veja como uma canção personalizada se compara com as opções tradicionais:',
      comparisons: [
        { label: '⌚ Relógio ou eletrônico', price: 'R$ 150–1000', duracao: '3–5 anos', emocao: 'Baixa' },
        { label: '👕 Roupa ou calçado', price: 'R$ 100–400', duracao: '1–2 anos', emocao: 'Baixa' },
        { label: '🍺 Jantar ou experiência', price: 'R$ 150–500', duracao: '2–3 horas', emocao: 'Média' },
        { label: '🍾 Bebida premium', price: 'R$ 80–300', duracao: '1 noite', emocao: 'Baixa' },
      ],
    },
    faqs: [
      {
        question: 'Meu marido é bem fechado e não costuma se emocionar. Vale a pena?',
        answer:
          'Justamente por isso a surpresa é tão especial. O homem reservado que ouve o próprio nome numa letra, que reconhece as memórias do casamento dentro da canção — esse é o tipo de coisa que rompe a armadura. Não tem garantia de lágrimas, mas há uma certeza: ele vai saber que você fez algo que só poderia ser feito para ele.',
      },
      {
        question: 'Posso pedir a música com um tom mais bem-humorado?',
        answer:
          'Sim! Se o marido tem senso de humor e vocês têm um relacionamento com muito riso, uma música que mencione as manias dele, as brigas bobas, as coisas engraçadas que ele faz — pode ser ainda mais emocionante do que uma balada. O humor com amor é muito poderoso.',
      },
      {
        question: 'Posso incluir histórias da família — os filhos, a casa, os momentos juntos?',
        answer:
          'Claro! A música pode ter o foco no marido como pai, como companheiro, como pessoa. Pode incluir os filhos, as conquistas da família, os momentos que marcaram a vida de vocês juntos. Quanto mais história você contar, mais rica e emocionante vai ficar a letra.',
      },
      {
        question: 'Funciona para homens de qualquer idade?',
        answer:
          'Sim! Temos músicas para aniversários de 30, 40, 50, 60 anos — cada uma com o tom que combina com a fase da vida. Para maridos mais jovens, o tom pode ser mais animado. Para os mais velhos, pode ter mais peso emocional e gratidão. Você decide o tom ao contar a história.',
      },
      {
        question: 'Posso enviar a música anonimamente — sem revelar que fui eu?',
        answer:
          'Pode! Você recebe o arquivo no seu e-mail e pode enviar como quiser — com o seu nome, de forma anônima, como se fosse um misterioso admirador. Mas prepare-se: quando ele escutar as memórias específicas de vocês dois na letra, ele provavelmente vai adivinhar quem mandou.',
      },
    ],
    estiloDestaque: 'Sertanejo',
    sazonal: false,
  },

  {
    slug: 'homenagem-para-avo',
    path: '/homenagem-para-avo',
    ocasiao: 'Homenagem',
    destinatario: 'avó',
    artigo: 'a',
    possessivo: 'sua',
    title: 'Homenagem Musical para a Avó — Música Personalizada | Inspira',
    description:
      'Faça uma homenagem musical exclusiva para a sua avó. Letra com as histórias dela, MPB, Bolero, Sertanejo. Entrega em até 30 min. A partir de R$ 34,90.',
    h1: 'Homenagem para a avó que tem uma vida inteira de amor para ser celebrada',
    heroSubtitle:
      'Avó é aquela pessoa que faz tudo parecer mais acolhedor só de estar presente. Uma música com a história dela — a cozinha que cheira a casa, as histórias que ela conta, o amor que atravessa gerações — é o presente mais especial que você vai dar na vida.',
    whySection: [
      'Avó tem um lugar especial no coração da família que é difícil de explicar. Não é só o carinho — é a história. É a vida inteira que ela viveu, os filhos que criou, os netos que viu crescer, os momentos que ela testemunhou e guardou. Uma homenagem musical para a avó é uma forma de dizer: a gente viu tudo isso, a gente sabe quem você é, e a gente quer celebrar isso com uma canção que seja só sua.',
      'A música para a avó pode contar a história de uma forma que vai muito além dos presentes convencionais. Você fala sobre o cheiro da casa dela, sobre a comida que ela faz que ninguém imita, sobre as histórias que ela conta que os netos já sabem de cor, sobre o jeito que ela recebe todo mundo com aquele abraço que parece de raiz. Nossa equipe compõe com base nessa história — com a letra que vai fazer ela fechar os olhos e sorrir, no estilo musical que ela cresceu ouvindo. Bolero? MPB? Sertanejo de raiz? Jovem Guarda? A música vai soar como o tempo em que ela era jovem.',
      'Uma homenagem musical para a avó funciona para qualquer data: aniversário dela, Dia dos Avós (julho), Natal, ou simplesmente porque você quis. A entrega é em até 30 minutos após o pagamento, direto no seu e-mail. Você pode colocar para tocar no almoço de família, encaminhar no grupo dos netos, ou presentear ela pessoalmente. Seja qual for o momento, é uma experiência que toda a família vai lembrar.',
    ],
    priceAnchor: {
      intro:
        'Para a avó que tem tudo que precisa mas que merece ser celebrada, veja como uma homenagem musical se destaca:',
      comparisons: [
        { label: '🌸 Flores e chocolates', price: 'R$ 80–150', duracao: '3–5 dias', emocao: 'Média' },
        { label: '🧣 Roupa ou acessório', price: 'R$ 80–300', duracao: 'Varia', emocao: 'Baixa' },
        { label: '☕ Cesta de café ou chá', price: 'R$ 60–150', duracao: '1–2 semanas', emocao: 'Baixa' },
        { label: '🖼️ Quadro de foto da família', price: 'R$ 60–200', duracao: 'Para sempre', emocao: 'Alta' },
      ],
    },
    faqs: [
      {
        question: 'A avó precisa de tecnologia para receber a música?',
        answer:
          'Não! A entrega é no seu e-mail. Você recebe o arquivo MP3 e pode fazer o que quiser: colocar para tocar no celular ao lado dela, transferir para o celular dela, gravar em um CD (ela provavelmente ainda tem CD player), ou colocar num pen drive. O importante é que ela escute — o meio você escolhe.',
      },
      {
        question: 'Posso incluir histórias de quando minha avó era jovem, mesmo que eu não tenha vivido isso?',
        answer:
          'Sim! Você pode contar histórias que ela mesma narrou para a família — sobre quando ela era criança, quando se casou, como criou os filhos. São histórias que fazem parte do legado familiar, e incluí-las na letra torna a música ainda mais especial, como se fosse uma celebração da vida inteira dela.',
      },
      {
        question: 'Funciona para homenagear uma bisavó?',
        answer:
          'Claro! Uma bisavó que ainda está presente merece ser celebrada. E se ela já partiu, uma música de homenagem póstuma é uma forma linda de preservar a memória dela para as gerações futuras. A canção pode ser compartilhada com a família inteira como um legado.',
      },
      {
        question: 'É possível fazer a homenagem em nome de todos os netos?',
        answer:
          'Sim! A música pode ser da perspectiva de "todos os netos" — pode mencionar os nomes de cada um, o que cada um representa para ela, a gratidão coletiva da família. Basta informar os nomes e o que você quer destacar de cada um ao fazer o pedido.',
      },
      {
        question: 'Posso usar estilos musicais mais antigos, como Bolero ou Jovem Guarda?',
        answer:
          'Com certeza! Esses são estilos que muitas avós adoram. Uma música de homenagem em Bolero ou no estilo Jovem Guarda tem aquele charme nostálgico que faz a avó fechar os olhos e viajar no tempo. É uma das combinações mais emocionantes que fazemos.',
      },
    ],
    estiloDestaque: 'Bolero',
    sazonal: false,
  },
];

export default landingPages;
